/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {nullify, ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import React, {useEffect, useState} from 'react';
import FailedPayment from "../../../models/failedPaymentModel";
import {PaymentFailureStatus} from "../../../util/interfaces";
import {createErrorMessage} from "../../../util/utils";
import FailedPayments from "./failedPayments";

export default function FailedPaymentsController() {
    const [payments, setPayments] = useState<Array<FailedPayment>>([]);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState<PaymentFailureStatus | null>(null);
    const [search, setSearch] = useState('');
    const [limit, setLimit] = useState(10);
    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(0);

    function onChangeSearch(value: string) {
        setSearch(value);
    }

    function onChangeStatus(_status: PaymentFailureStatus) {
        return function () {
            const nextStatus = _status === status ? null : _status;
            setStatus(nextStatus);
            loadPayments({status: nextStatus});
        }
    }

    function onChangeLimit(value: number) {
        if (value === limit) return;
        setLimit(value);
        loadPayments({limit: value});
    }

    function onChangePage(nextPage: number) {
        return function () {
            setPage(nextPage);
            loadPayments({page: nextPage});
        }
    }

    function onGenerateInvoice(payment: FailedPayment, sendImmediately: boolean) {
        return function () {
            if (!!payment.newInvoiceId) return;  // Invoice already generated
            setLoading(true);
            payment.adminGenerateInvoice({
                sendImmediately,
                callback: (payment: FailedPayment, success: boolean, data: ResponseData) => {
                    if (success) {
                        loadPayments();
                        showSnackbar('De factuur is gegeneerd en verzonden')
                    } else {
                        setLoading(false);
                        showDialog(createErrorMessage(data.message ?? 'Het genereren van de factuur is niet gelukt.'),
                            'Factuur genereren mislukt');
                    }
                }
            });
        }
    }

    function onSendInvoice(payment: FailedPayment) {
        return function () {
            if (!payment.paymentUrl) return;
            setLoading(true);
            payment.send((payment: FailedPayment, success: boolean, data: ResponseData) => {
                if (success) {
                    showSnackbar('De factuur is verzonden');
                    loadPayments();
                } else {
                    setLoading(false);
                    showDialog(createErrorMessage(data.message ?? 'Het verzenden van de factuur is niet gelukt.'),
                        'Factuur verzenden mislukt');
                }
            })
        }
    }

    function onDeletePayment(payment: FailedPayment) {
        return function () {
            setLoading(true);
            payment.delete((payment: FailedPayment, success: boolean, data: ResponseData) => {
                if (success) {
                    showSnackbar('De betaling is verwijderd');
                    loadPayments();
                } else {
                    setLoading(false);
                    showDialog(createErrorMessage(data.message ?? 'Het verwijderen van de betaling is niet gelukt.'),
                        'Verwijderen mislukt');
                }
            })
        }
    }

    function onEnterSearch() {
        setPage(0);
        loadPayments({page: 0});
    }

    function loadPayments(params?: {
        status?: PaymentFailureStatus | null;
        search?: string;
        limit?: number;
        page?: number
    }) {
        setLoading(true);
        const _status = params?.status !== undefined ? params.status : status;
        const _search = params?.search ?? search;
        const _limit = params?.limit ?? limit;
        const _page = params?.page ?? page;
        FailedPayment.getAll({
            page: _page,
            status: _status,
            limit: _limit,
            search: nullify(_search),
            callback: (payments: Array<FailedPayment>, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    setPayments(payments);
                    setTotal(data.total);
                } else
                    showDialog(createErrorMessage(data.message ??
                        'Het laden van de mislukte betalingen is niet gelukt.'), 'Laden mislukt');
            }
        })
    }

    useEffect(() => {
        loadPayments();
    }, []);

    return (
        <FailedPayments onChangeSearch={onChangeSearch} onChangeStatus={onChangeStatus} onChangeLimit={onChangeLimit}
                        onChangePage={onChangePage} onGenerateInvoice={onGenerateInvoice} onSendInvoice={onSendInvoice}
                        onDeletePayment={onDeletePayment} onEnterSearch={onEnterSearch} payments={payments}
                        loading={loading} status={status} search={search} limit={limit} page={page} total={total}/>
    )
}